import { getIntegrationBySlug } from '@deepguide-ai/types'

/**
 * Single source of truth for `/vendor-logos/*` brand assets.
 *
 * Brand resolution uses local asset overrides and the integration registry.
 * - `vendorLogo(name)` — resolves a free-text vendor/subprocessor NAME (vendors
 *   table, trust center) to a logo path. Hide the <img> on error (404) so
 *   vendors without a logo degrade gracefully.
 * - `vendorLogoPath(key)` — resolves integration/provider keys to paths.
 * - `getVendorLogo(key)` — resolves known assets with dark-mode styling.
 *
 * Add the file to `web/public/vendor-logos/` and its path to the integration
 * registry, or `VENDOR_LOGO_FILES` for an asset override. Don't hardcode paths
 * in components.
 */

/**
 * Asset overrides and brands outside the integration registry, keyed by their
 * canonical vendor name. Callers never have to guess `.svg` vs `.png`.
 */
const VENDOR_LOGO_FILES: Record<string, string> = {
  // SVG assets
  freshdesk: 'freshdesk-mark.svg',
  shortcut: 'shortcut.svg',
  trello: 'trello.svg',
  wrike: 'wrike.ico',
  dropbox: 'dropbox.svg',
  notion: 'notion.svg',
  confluence: 'confluence.svg',
  clickup: 'clickup.svg',
  asana: 'asana.svg',
  zendesk: 'zendesk.svg',
  servicenow: 'servicenow.svg',
  box: 'box.svg',
  snowflake: 'snowflake.svg',
  monday: 'monday.svg',
  aikido: 'aikido.svg',
  okta: 'okta.svg',
  auth0: 'auth0.svg',
  datadog: 'datadog.svg',
  sentry: 'sentry.svg',
  pagerduty: 'pagerduty.svg',
  vercel: 'vercel.svg',
  render: 'render.svg',
  jumpcloud: 'jumpcloud.svg',
  onelogin: 'onelogin.svg',
  snyk: 'snyk.svg',
  quickbooks: 'quickbooks.svg',
  cloudflare: 'cloudflare.svg',
  tencent: 'tencent.svg',
  'tencent-cloud': 'tencent.svg',
  aliyun: 'aliyun.svg',
  'alibaba-cloud': 'aliyun.svg',
  supabase: 'supabase.svg',
  // png-only asset — without this row the `.svg` fallback 404s.
  inngest: 'inngest.png',
  azure_devops: 'azure_devops.svg',
  // svg-only asset — without this row the fallback guesses `.png` and 404s.
  kubernetes: 'kubernetes.svg',
  microsoft_sharepoint: 'microsoft_sharepoint.svg',
  wiz: 'wiz.svg',
  bamboohr: 'bamboohr.svg',
  rippling: 'rippling.svg',
  deel: 'deel.svg',
  gusto: 'gusto.svg',
  hibob: 'hibob.svg',
  adp: 'adp.svg',
  workday: 'workday.svg',
  successfactors: 'sap_successfactors.svg',
  stripe: 'stripe.svg',
  brex: 'brex.svg',
  salesforce: 'salesforce.svg',
  hubspot: 'hubspot.svg',
  zoom: 'zoom.svg',
  jamf: 'jamf.svg',
  'open-attest': 'open-attest.svg',
  claude: 'claude.svg',
  'better-stack': 'better_stack.svg',
  'mx-technologies': 'mx_technologies.svg',
  // PNG-only assets
  axiom: 'axiom.png',
  azure: 'azure.png',
  'google-cloud': 'google-cloud.png',
  'google-workspace': 'google-workspace.png',
  crowdstrike: 'crowdstrike.png',
  checkr: 'checkr.png',
  incident_io: 'incident-io.png',
  incidentio: 'incident-io.png',
  'incident-io': 'incident-io.png',
  kandji: 'kandji.png',
  knowbe4: 'knowbe4.png',
}

/**
 * Alias map for free-text NAMES whose slug doesn't match a file (Microsoft
 * family lives at the web root; AWS/GSuite naming variants). Values are final
 * paths.
 */
const VENDOR_LOGO_ALIASES: Record<string, string> = {
  openattest: '/vendor-logos/open-attest.svg',
  mondaycom: '/vendor-logos/monday.svg',
  'sap-successfactors': '/vendor-logos/sap_successfactors.svg',
  'adp-workforce-now': '/vendor-logos/adp.svg',
  // Microsoft 365 family — the plain Microsoft mark lives at the web root.
  microsoft: '/microsoft.svg',
  'microsoft-365': '/microsoft.svg',
  'microsoft365': '/microsoft.svg',
  'office-365': '/microsoft.svg',
  office365: '/microsoft.svg',
  m365: '/microsoft.svg',
  // No product marks for these; the vendor is Microsoft.
  'azure-entra-id': '/microsoft.svg',
  'microsoft-intune': '/microsoft.svg',
  'microsoft-azure': '/vendor-logos/azure.png',
  'azure-monitor': '/vendor-logos/azure.png',
  'azure-postgresql': '/vendor-logos/azure.png',
  'azure-database-for-postgresql': '/vendor-logos/azure.png',
  'amazon-web-services': '/vendor-logos/aws.png',
  amazon: '/vendor-logos/aws.png',
  'aws-rds': '/vendor-logos/aws.png',
  'aws-ses': '/vendor-logos/aws.png',
  'cloudflare-r2': '/vendor-logos/cloudflare.svg',
  'github-issues': '/vendor-logos/github.png',
  // Google Cloud's asset is `google-cloud.png`, but the provider key is `gcp`
  // (and "Google Cloud" slugifies to `google-cloud`) — map both to the file.
  gcp: '/vendor-logos/google-cloud.png',
  'google-cloud': '/vendor-logos/google-cloud.png',
  googlecloud: '/vendor-logos/google-cloud.png',
  'google-cloud-platform': '/vendor-logos/google-cloud.png',
  'google-workspace': '/vendor-logos/google-workspace.png',
  // The population provider key is `google_workspace`; vendorLogo() strips the
  // underscore to `googleworkspace`, so map that collapsed form too.
  googleworkspace: '/vendor-logos/google-workspace.png',
  gsuite: '/vendor-logos/google-workspace.png',
  // vendorLogo() strips the underscore from `azure_devops` to `azuredevops`,
  // which never matches the registry's underscore key — map the collapsed form.
  azuredevops: '/vendor-logos/azure_devops.svg',
}

const DARK_MODE_MONOCHROME_LOGOS = new Set([
  '/vendor-logos/auth0.svg',
  '/vendor-logos/zendesk.svg',
  '/vendor-logos/open-attest.svg',
  '/vendor-logos/render.svg',
  '/vendor-logos/sentry.svg',
])

/** Known brand artwork and its theme treatment; unknown keys have no logo. */
export function getVendorLogo(key: string): { src: string; className?: string } | null {
  const normalizedKey = key.replaceAll('_', '-')
  const file = VENDOR_LOGO_FILES[key] ?? VENDOR_LOGO_FILES[normalizedKey]
  const src = file
    ? `/vendor-logos/${file}`
    : VENDOR_LOGO_ALIASES[key] ?? VENDOR_LOGO_ALIASES[normalizedKey] ?? getIntegrationBySlug(normalizedKey)?.logoPath
  if (!src) return null
  return {
    src,
    className: DARK_MODE_MONOCHROME_LOGOS.has(src)
      ? 'dark:brightness-0 dark:invert'
      : undefined,
  }
}

/** Resolve a provider key, keeping the SVG fallback for unregistered assets. */
export function vendorLogoPath(key: string): string {
  return getVendorLogo(key)?.src ?? `/vendor-logos/${key}.svg`
}

export function vendorLogo(name: string | null | undefined): string | null {
  if (!name) return null
  const slug = name
    .toLowerCase()
    .replace(/[\s/]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
  if (!slug) return null
  return getVendorLogo(slug)?.src ?? `/vendor-logos/${slug}.png`
}
